import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { HYDRATE } from 'next-redux-wrapper';
import { useRouter } from 'next/router';
import { getBookingTexts } from '../lib/sanity/fetchBookingTexts';

const BASE_URL = 'https://statics.pinchos.se/api/';
const API_VERSION = '4';

function createTestVenue(demoVenue: Venue) {
  const testVenue = { ...demoVenue } as Venue;
  testVenue.id = process.env.NEXT_PUBLIC_E2E_SANDBOX_VENUE_ID;
  testVenue.extData.venueNode.id = process.env.NEXT_PUBLIC_E2E_SANDBOX_VENUE_ID;
  testVenue.relatedPlaces = [];
  return testVenue;
}

async function addDemoVenue(response, meta, { lang, slim }) {
  const demoVenueResponse = await fetch(
    `${BASE_URL}venue/${lang}/se-hq-it${slim ? '.slim' : ''}.${API_VERSION}.json`,
  );
  const demoVenue = await demoVenueResponse.json();

  if (process.env.NEXT_PUBLIC_E2E_TEST === 'true') {
    return [demoVenue, createTestVenue(demoVenue), ...response];
  }

  return [demoVenue, ...response];
}

// Define a service using a base URL and expected endpoints
/**
 * Utilizes @reduxjs/toolkit createApi to create an api service.
 * @see https://redux-toolkit.js.org/rtk-query/api/createApi
 */
export const s3Api = createApi({
  reducerPath: 's3Api',
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: ['Venues', 'Venue'],
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      return action.payload[reducerPath];
    }
  },
  endpoints: (builder) => ({
    getVenuesList: builder.query<Venue[], { lang: string; slim: boolean }>({
      providesTags: ['Venues'],
      query: ({ lang, slim }) => `venues/${lang}/all${slim ? '.slim' : ''}.${API_VERSION}.json`,
      transformResponse: process.env.NODE_ENV === 'development' ? addDemoVenue : undefined,
      // This is the full multi-venue catalog (~1MB as of 2026-08, all.slim.4.json
      // grew from ~260KB in 2020) and it changes on the catalog sync cadence
      // (roughly every 10 minutes, see runbook-stale-price-in-app), not on every
      // render. With keepUnusedDataFor: 0 (the old value) RTK Query evicted the
      // cache the instant the last subscriber unmounted, so a guest bouncing
      // between ContactForm and choose-time - both of which render
      // <RelatedVenues>, which calls this query - redownloaded the whole
      // catalog every single navigation. This is the traffic behind Sentry
      // issue BOOKING-CLIENT-2N3 ('Large HTTP payload' on choose-time).
      // 300s keeps it around across a single booking session without risking
      // meaningfully stale data.
      keepUnusedDataFor: 300,
    }),
    getVenueById: builder.query<Venue, { lang: string; venueId: string | string[]; slim: boolean }>(
      {
        // Deliberately 0, unlike getVenuesList. Step-to-step navigation keeps the
        // entry anyway (the next page subscribes before eviction runs), so a
        // longer window only matters when a guest comes back after a gap - and
        // then the date page would render the cached openHours while
        // freshVenueQueryOptions refetches (it only shows LoadingOverlay when
        // openHours is missing), i.e. the stale calendar flash fixed in 547182fe.
        keepUnusedDataFor: 0,
        query: ({ lang, venueId, slim }) => {
          const id = process.env.NEXT_PUBLIC_E2E_TEST === 'true' ? 'se-hq-it' : venueId;
          return {
            url: `venue/${lang}/${id}${slim ? '.slim' : ''}.${API_VERSION}.json`,
            cache: 'no-store',
          };
        },
        transformResponse: async (response: Venue, _, { lang }) => {
          const transformedResponse = { ...response };

          // Remove unused properties
          delete transformedResponse.payment;
          delete transformedResponse.alcoholAgeLimit;
          delete transformedResponse.billingAllowed;
          // delete transformedResponse.openHours;
          delete transformedResponse.takeAwayEnabled;
          // delete transformedResponse.location;
          delete transformedResponse.showGuestFeedback;
          delete transformedResponse.showRating;
          delete transformedResponse.urls.venue;
          delete transformedResponse.urls.editorialV2;
          delete transformedResponse.urls.jobApplication;
          delete transformedResponse.urls.venuePage;
          delete transformedResponse.urls.menuIntroPage;

          // Sister venues from the booking URL's ?places= (legacy placeIds, see
          // venuesForPlaceIds), the fallback for when booking-api's city pool
          // cannot be fetched (sisterVenueIds). Not the booking id: that is the
          // venue's own id, the slug (useGetBookingVenueId).
          const url = new URL(transformedResponse?.urls?.booking);
          const preferredPlace = url.searchParams.get('preferredPlace');
          const places =
            url.searchParams.get('places')?.length > 0
              ? url.searchParams.get('places')?.split(',')
              : [];
          transformedResponse.relatedPlaces =
            places?.length > 1 ? places.filter((place) => place !== preferredPlace) : [];

          // Remove unused booking url
          delete transformedResponse.urls.booking;

          transformedResponse.bookingTexts = await getBookingTexts(response.id, lang);

          if (process.env.NEXT_PUBLIC_E2E_TEST === 'true') {
            return createTestVenue(transformedResponse);
          }

          return transformedResponse;
        },
        providesTags: ['Venue'],
      },
    ),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useGetVenueByIdQuery,
  useGetVenuesListQuery,
  util: { getRunningQueriesThunk },
} = s3Api;

/** Refetch venue on mount — use in booking flow only, not general-purpose hooks below. */
export const freshVenueQueryOptions = { refetchOnMountOrArgChange: true as const };

export function useGetVenueTitle(locale) {
  const {
    query: { venueId },
    isFallback,
  } = useRouter();

  const result = useGetVenueByIdQuery(
    { lang: locale, venueId: venueId as string, slim: true },
    { skip: isFallback || !venueId?.length },
  );
  const { isSuccess, data } = result;

  if (isSuccess) {
    return data.title;
  }
  return venueId as string;
}

export function useGetVenueAdress(locale: string, overrideVenueId?: string) {
  const {
    query: { venueId },
    isFallback,
  } = useRouter();

  const id = overrideVenueId || venueId;

  const result = useGetVenueByIdQuery(
    { lang: locale, venueId: id, slim: true },
    { skip: isFallback || !id?.length },
  );
  const { isSuccess, data } = result;

  if (isSuccess) {
    return formatVenueAddress(data);
  }
  return null;
}

export function formatVenueAddress(data: Venue) {
  return `${data?.visitationAddress?.addressLine1 || 'Address unknown'}`;
}

export function useGetVenueTexts(locale: string, overrideVenueId?: string) {
  const {
    query: { venueId },
    isFallback,
  } = useRouter();

  const id = overrideVenueId || venueId;

  const result = useGetVenueByIdQuery(
    { lang: locale, venueId: id, slim: true },
    { skip: isFallback || !id?.length },
  );
  const { data } = result;

  return data?.bookingTexts || { pre: null, post: null };
}

export function useGetVenueName(locale: string, overrideVenueId?: string) {
  const {
    query: { venueId },
    isFallback,
  } = useRouter();

  const id = overrideVenueId || venueId;

  const result = useGetVenueByIdQuery(
    { lang: locale, venueId: id, slim: true },
    { skip: isFallback || !id?.length },
  );
  const { isSuccess, data } = result;
  if (isSuccess) {
    return data.brandName + ' ' + data.title;
  }
  return null;
}

/**
 * The venue the booking flow is on (the route's venueId), from the catalog.
 */
export function useGetBookingVenue(): Venue | null {
  const {
    locale,
    query: { venueId },
    isFallback,
  } = useRouter();

  const result = useGetVenueByIdQuery(
    { lang: locale, venueId: venueId as string, slim: true },
    { skip: isFallback || !venueId?.length },
  );
  const { isSuccess, data } = result;
  return isSuccess ? data : null;
}

/**
 * The id booking-api knows the booking flow's venue by: the catalog venue's own
 * id, the slug ("se-gbg-heden"), which is booking-api's Place.venueId. Not
 * extData.venueNode.id, the legacy placeId ("Heden") (booking-api #48 step 7).
 *
 * Read from the catalog rather than kept in the booking params, which are
 * persisted in localStorage, where an id from an earlier visit (or release)
 * could linger.
 */
export function useGetBookingVenueId(): string | null {
  return useGetBookingVenue()?.id || null;
}

export function useGetCountryCode() {
  const {
    query: { venueId },
    isFallback,
  } = useRouter();

  const id = venueId;

  const result = useGetVenueByIdQuery(
    { lang: 'en', venueId: id, slim: true },
    { skip: isFallback || !id?.length },
  );
  const { isSuccess, data } = result;
  if (isSuccess) {
    return data.countryCode;
  }
  return null;
}

export const { getVenueById, getVenuesList } = s3Api.endpoints;
